import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  IconButton,
  Collapse,
  Alert,
  Divider,
  Tooltip
} from '@mui/material';
import {
  MoneyOff as DebtIcon,
  Warning as WarningIcon,
  Schedule as OverdueIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllDebts } from '../../services/debtService';
import { Debt, DebtStatus } from '../../utils/debtTypes';
import { formatCurrency } from '../../utils/formatters';

interface QuickDebtAlertsProps {
  maxItems?: number;
  showExpanded?: boolean;
}

interface DebtAlert {
  id: string;
  debt: Debt;
  type: 'overdue' | 'due_soon' | 'high_amount';
  priority: 'high' | 'medium' | 'low';
  message: string;
  daysInfo: string;
}

const QuickDebtAlerts: React.FC<QuickDebtAlertsProps> = ({ 
  maxItems = 5, 
  showExpanded = false 
}) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<DebtAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(showExpanded);

  useEffect(() => {
    loadDebtAlerts();
  }, []);

  const loadDebtAlerts = async () => {
    try {
      setLoading(true);

      // جلب جميع الديون ثم فلترتها محلياً
      console.log('🔍 جلب جميع الديون للتنبيهات السريعة...');
      const allDebtsFromDB = await getAllDebts(undefined, 200);
      console.log(`📊 تم جلب ${allDebtsFromDB.length} دين من قاعدة البيانات`);

      // فلترة الديون النشطة والمسددة جزئياً
      const allDebts = allDebtsFromDB.filter(debt =>
        debt.status === DebtStatus.ACTIVE || debt.status === DebtStatus.PARTIALLY_PAID
      );
      console.log(`✅ الديون النشطة والمسددة جزئياً: ${allDebts.length}`);
      const debtAlerts: DebtAlert[] = [];
      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

      allDebts.forEach(debt => {
        // الديون المتأخرة
        if (debt.dueDate && new Date(debt.dueDate) < now) {
          const daysOverdue = Math.floor((now.getTime() - new Date(debt.dueDate).getTime()) / (1000 * 60 * 60 * 24));
          debtAlerts.push({
            id: `overdue-${debt.id}`,
            debt,
            type: 'overdue',
            priority: daysOverdue > 7 ? 'high' : 'medium',
            message: `${debt.customerName} - ${formatCurrency(debt.remainingAmount)}`,
            daysInfo: `متأخر ${daysOverdue} يوم`
          });
        }
        
        // الديون القريبة من الاستحقاق
        else if (debt.dueDate && new Date(debt.dueDate) > now && new Date(debt.dueDate) <= threeDaysFromNow) {
          const daysUntilDue = Math.ceil((new Date(debt.dueDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          debtAlerts.push({
            id: `due-soon-${debt.id}`,
            debt,
            type: 'due_soon',
            priority: daysUntilDue <= 1 ? 'high' : 'medium',
            message: `${debt.customerName} - ${formatCurrency(debt.remainingAmount)}`,
            daysInfo: `يستحق خلال ${daysUntilDue} يوم`
          });
        }
        
        // الديون عالية القيمة
        else if (debt.remainingAmount >= 10000) {
          debtAlerts.push({
            id: `high-amount-${debt.id}`,
            debt,
            type: 'high_amount',
            priority: debt.remainingAmount >= 20000 ? 'high' : 'medium',
            message: `${debt.customerName} - ${formatCurrency(debt.remainingAmount)}`,
            daysInfo: 'دين عالي القيمة'
          });
        }
      });

      // ترتيب التنبيهات حسب الأولوية
      const sortedAlerts = debtAlerts
        .sort((a, b) => {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          }
          return new Date(b.debt.createdAt).getTime() - new Date(a.debt.createdAt).getTime();
        })
        .slice(0, maxItems);

      setAlerts(sortedAlerts);
    } catch (error) {
      console.error('Error loading debt alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDebt = (debtId: string) => {
    navigate(`/admin/debts?highlight=${debtId}`);
  };

  const handleCallCustomer = (phone: string) => {
    window.open(`tel:${phone}`, '_self');
  };

  const getAlertColor = (type: string, priority: string) => {
    if (type === 'overdue') return 'error';
    if (type === 'due_soon') return priority === 'high' ? 'error' : 'warning';
    if (type === 'high_amount') return 'info';
    return 'default';
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'overdue':
        return <OverdueIcon />;
      case 'due_soon':
        return <WarningIcon />;
      case 'high_amount':
        return <DebtIcon />;
      default:
        return <DebtIcon />;
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          جاري تحميل تنبيهات الديون...
        </Typography>
      </Paper>
    );
  }

  if (alerts.length === 0) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DebtIcon sx={{ mr: 1 }} />
          <Typography variant="body2" fontWeight="bold">
            لا توجد تنبيهات ديون عاجلة 👍
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'warning.light', 
        color: 'warning.contrastText',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WarningIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            تنبيهات الديون العاجلة
          </Typography>
          <Chip 
            label={alerts.length} 
            size="small" 
            sx={{ ml: 1, bgcolor: 'warning.dark', color: 'warning.contrastText' }}
          />
        </Box>
        
        <IconButton 
          size="small" 
          onClick={() => setExpanded(!expanded)}
          sx={{ color: 'warning.contrastText' }}
        >
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Alerts List */}
      <Collapse in={expanded}>
        <List sx={{ p: 0 }}>
          {alerts.map((alert, index) => (
            <React.Fragment key={alert.id}>
              <ListItem
                sx={{
                  py: 1,
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      p: 0.5,
                      borderRadius: '50%',
                      bgcolor: `${getAlertColor(alert.type, alert.priority)}.light`,
                      color: `${getAlertColor(alert.type, alert.priority)}.main`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {getAlertIcon(alert.type)}
                  </Box>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight="bold">
                      {alert.message}
                    </Typography>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        label={alert.daysInfo}
                        size="small"
                        color={getAlertColor(alert.type, alert.priority)}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                      {alert.debt.customerPhone && (
                        <Chip
                          label={alert.debt.customerPhone}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  }
                />
                
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {alert.debt.customerPhone && (
                    <Tooltip title="اتصال">
                      <IconButton 
                        size="small" 
                        onClick={() => handleCallCustomer(alert.debt.customerPhone)}
                        sx={{ color: 'success.main' }}
                      >
                        <PhoneIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="عرض التفاصيل">
                    <IconButton 
                      size="small" 
                      onClick={() => handleViewDebt(alert.debt.id)}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
              
              {index < alerts.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        
        {/* Footer */}
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/admin/debts')}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            عرض جميع الديون
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default QuickDebtAlerts;
